import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button, Dropdown, Menu } from 'antd';

import logo from 'assets/images/logo.svg';
import MobileMenuIcon from 'assets/images/mobile-menu.svg';
import { useBreakpoint, useKeycloakReady } from 'hooks';

import { LanguageSelector, Link, MenuWrapper, StyledDivider, StyledHeader } from '../BravoHeader/components';
import { sessionStorageService } from 'utils/SessionStorageService';
import { removeCookie } from 'utils/cookies';

interface MenuInfo {
  key: string;
  keyPath: string[];
  domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;
}

const menuItems = [
  { path: 'admin', i18nKey: 'viewAsUser' },
  { path: 'admin/system-announcement-message', i18nKey: 'systemAnnouncementMessage' },
];

const BravoAdminHeader = (): JSX.Element => {
  const { t } = useTranslation();
  const { keycloak } = useKeycloakReady();
  const [selectedKey, setSelectedKey] = useState('');
  const location = useLocation();
  const { md } = useBreakpoint();
  const isLogin =
    keycloak.authenticated &&
    !['/we-care-login', '/register/otp-verification', '/migration'].includes(location?.pathname);

  const getSelectedKey = () => {
    if (menuItems.every((item) => !location.pathname.includes(item.path))) {
      return '';
    }

    return selectedKey;
  };

  const handleMenuClick = ({ key }: MenuInfo) => {
    setSelectedKey(key);
  };

  // Refer to: Usage upgrade after 4.20.0 (https://ant.design/components/menu/#Usage-upgrade-after-4.20.0)
  const getItems = () => {
    return menuItems.map(({ path, i18nKey }, index) => ({
      key: `${path}-${index}`,
      label: <Link to={`/${path}`}>{t(`admin.${i18nKey}.title`)}</Link>,
    }));
  };

  const onLogoutClicked = async () => {
    try {
      await keycloak.logout();
      sessionStorageService.clear();
      removeCookie('KeycloakTokens');
    } catch (error) {
      console.log('onLogoutPressed()->Error occurred', error);
    }
  };

  return (
    <StyledHeader>
      <img src={logo} alt="" width="200" />
      {md && (
        <MenuWrapper size={16}>
          {isLogin && (
            <>
              <Menu
                selectedKeys={[getSelectedKey()]}
                onClick={handleMenuClick}
                mode="horizontal"
                disabledOverflow
                items={getItems()}
              ></Menu>
              <StyledDivider type="vertical" />
            </>
          )}
          <LanguageSelector white={false} />
          {isLogin && (
            <Button type="link" onClick={onLogoutClicked}>
              {t('settings.logout')}
            </Button>
          )}
        </MenuWrapper>
      )}
      {!md && (
        <MenuWrapper>
          <Dropdown
            overlay={
              <Menu
                items={[
                  ...(isLogin ? getItems().filter((o) => o.key !== 'help-4') : []),
                  {
                    key: 'lang',
                    label: <LanguageSelector white={false} />,
                  },
                ]}
              />
            }
            trigger={['click']}
          >
            <img src={MobileMenuIcon} alt="mobile menu" />
          </Dropdown>
        </MenuWrapper>
      )}
    </StyledHeader>
  );
};

export default BravoAdminHeader;
